import React from 'react';

import { validEmailRegex } from 'constants/regex';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import TextField from 'components/application/TextField';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import SelectedPersonnelCard from 'components/settings/users/SelectedPersonnelCard';

export default function UserDetailsEditor({
  currentUser,
  formContext,
  isEmailFieldEditable,
  onDetailsChange,
  onSelectionEdit,
  onSelectionReset,
  removeErrorStyling,
  requestError,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UserDetailsEditor',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const emailTextFieldFormContextProps =
    formContext === 'new' ?
      {
        isDisabled: true,
        clearable: true,
        onClear: () => onSelectionReset({ emailOnly: true }),
        onChange: () => undefined,
      }
    : {
        onChange: (e) =>
          onDetailsChange({ attribute: 'email', value: e.target.value }),
      };

  return (
    <>
      {currentUser.isDerived && (
        <SelectedPersonnelCard
          email={currentUser.email}
          firstName={currentUser.firstName}
          lastName={currentUser.lastName}
          onClose={() => onSelectionReset()}
          onEdit={onSelectionEdit}
          profilePhotoUrl={currentUser.profilePhotoUrl}
        />
      )}
      {isEmailFieldEditable && !currentUser.isDerived && (
        <div className='tooltip-parent'>
          <TextField
            fieldError={requestError.validationErrors.email}
            isRequired={true}
            label={i18nTShared('emailAddress')}
            name='email'
            placeholder=''
            removeErrorStyling={removeErrorStyling}
            value={currentUser.email}
            {...emailTextFieldFormContextProps}
          />
          {!validEmailRegex.test(currentUser.email) && (
            <div className='tw-mb-3 tw-text-s tw-tracking-wide tw-text-red-800'>
              {i18nT('email.invalid')}
            </div>
          )}
        </div>
      )}
      {!currentUser.isDerived && (
        <>
          <TextField
            fieldError={requestError.validationErrors.firstName}
            isRequired={true}
            label={i18nTShared('firstName')}
            name='firstName'
            onChange={(e) =>
              onDetailsChange({ attribute: 'firstName', value: e.target.value })
            }
            removeErrorStyling={removeErrorStyling}
            value={currentUser.firstName}
          />
          <TextField
            fieldError={requestError.validationErrors.lastName}
            isRequired={true}
            label={i18nTShared('lastName')}
            name='lastName'
            onChange={(e) =>
              onDetailsChange({ attribute: 'lastName', value: e.target.value })
            }
            removeErrorStyling={removeErrorStyling}
            value={currentUser.lastName}
          />
        </>
      )}
      <TextField
        label={i18nTShared('position')}
        name='user[position]'
        onChange={(e) =>
          onDetailsChange({ attribute: 'position', value: e.target.value })
        }
        placeholder={i18nT('position.placeholder')}
        value={currentUser.position || ''}
      />
      <div className='-tw-mt-1.5 tw-text-s tw-font-normal tw-tracking-wide'>
        {i18nT('position.description', {
          interpolation: { escapeValue: false },
          singularRAMSName: currentActor.division.attributes.singularRAMSName,
        })}
      </div>
    </>
  );
}

UserDetailsEditor.propTypes = {
  currentUser: PropTypes.object.isRequired,
  formContext: PropTypes.string.isRequired,
  isEmailFieldEditable: PropTypes.bool.isRequired,
  requestError: PropTypes.object.isRequired,
  removeErrorStyling: PropTypes.func.isRequired,
  onSelectionReset: PropTypes.func.isRequired,
  onSelectionEdit: PropTypes.func.isRequired,
  onDetailsChange: PropTypes.func.isRequired,
};
